// API utility functions for TADABER Workforce
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cache system for better performance
const dataCache = {
  suppliers: { data: null, timestamp: 0, ttl: 30000 },
  cases: { data: null, timestamp: 0, ttl: 30000 },
  visas: { data: null, timestamp: 0, ttl: 30000 },
  tickets: { data: null, timestamp: 0, ttl: 60000 }
};

const isCacheValid = (cacheKey) => {
  const cache = dataCache[cacheKey];
  if (!cache || !cache.data) return false;
  return (Date.now() - cache.timestamp) < cache.ttl;
};

const setCache = (cacheKey, data) => {
  if (dataCache[cacheKey]) {
    dataCache[cacheKey].data = data;
    dataCache[cacheKey].timestamp = Date.now();
  }
};

export const invalidateCache = (cacheKey) => {
  if (dataCache[cacheKey]) {
    dataCache[cacheKey].data = null;
    dataCache[cacheKey].timestamp = 0;
  }
};

export const invalidateAllCache = () => {
  Object.keys(dataCache).forEach(key => invalidateCache(key));
};

// Get auth headers from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Create abort controller with timeout (cross-browser compatible)
const createTimeoutController = (timeout = 15000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  return { controller, timeoutId };
};

// API GET with retry logic
export const apiGet = async (endpoint, useCache = false, cacheKey = null) => {
  // Check cache first
  if (useCache && cacheKey && isCacheValid(cacheKey)) {
    return dataCache[cacheKey].data;
  }

  const maxRetries = 2;
  let lastError = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const { controller, timeoutId } = createTimeoutController(15000);
    
    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: {
          ...getAuthHeaders(),
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.error('Authentication error:', response.status);
          return [];
        }
        throw new Error(`HTTP ${response.status}: Network response was not ok`);
      }

      const data = await response.json();
      
      // Update cache if cacheKey provided
      if (cacheKey) {
        setCache(cacheKey, data);
      }

      return data || [];
    } catch (error) {
      clearTimeout(timeoutId);
      lastError = error;
      console.warn(`API GET attempt ${attempt + 1} failed for ${endpoint}:`, error.message);
      if (attempt < maxRetries) {
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
  }
  console.error('API GET Error after retries:', lastError);
  return [];
};

// API POST
export const apiPost = async (endpoint, data) => {
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(errorData.detail || 'Network response was not ok');
    }

    invalidateAllCache();
    return await response.json();
  } catch (error) {
    console.error('API POST Error:', error);
    throw error;
  }
};

// API PUT
export const apiPut = async (endpoint, data) => {
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(errorData.detail || 'Network response was not ok');
    }

    invalidateAllCache();
    return await response.json();
  } catch (error) {
    console.error('API PUT Error:', error);
    throw error;
  }
};

// API DELETE
export const apiDelete = async (endpoint) => {
  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(errorData.detail || 'Network response was not ok');
    }

    invalidateAllCache();
    return await response.json();
  } catch (error) {
    console.error('API DELETE Error:', error);
    throw error;
  }
};

// Cached data fetchers
export const getSuppliers = async (forceRefresh = false) => {
  return apiGet('/api/suppliers', !forceRefresh, 'suppliers');
};

export const getCases = async (forceRefresh = false) => {
  // Photos are lazy-loaded via /api/cases/{id}/photo to avoid re-downloading ~12MB every 30s
  return apiGet('/api/cases?include_photos=false', !forceRefresh, 'cases');
};

export const getVisas = async (forceRefresh = false) => {
  return apiGet('/api/visas', !forceRefresh, 'visas');
};

export const getTickets = async (forceRefresh = false) => {
  return apiGet('/api/tickets', !forceRefresh, 'tickets');
};
